import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import DrillCanvas from "./DrillCanvas";
import { MOVES } from "../data/moves";
import { useSettings } from "../context/SettingsContext";
import { track } from "../lib/analytics";
import { motion } from "framer-motion";
import { Target, AlertTriangle, Trophy, Zap, User, Timer, ChevronRight, PlayCircle } from "lucide-react";

const ytSearch = (name) => `https://www.youtube.com/results?search_query=${encodeURIComponent(`how to ${name} soccer skill tutorial`)}`;

const youtubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5" aria-label={`Difficulty ${n} of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className={`text-sm ${i <= n ? "text-[#C41E24]" : "text-white/15"}`}>★</span>
    ))}
  </span>
);

const TIER_LABEL = { touch: "Foundational Touch", move: "Signature Move", combo: "Combination / Flow" };

const MoveCard = ({ move, onOpen }) => (
  <motion.button
    data-testid={`move-card-${move.id}`}
    onClick={() => onOpen(move)}
    whileHover={{ y: -4 }}
    className="group text-left bg-[#141414] border border-white/10 hover:border-[#C41E24]/60 rounded-lg p-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
  >
    <div className="flex items-center justify-between mb-3">
      <Stars n={move.difficulty} />
      <ChevronRight size={16} className="text-white/30 group-hover:text-[#C41E24] transition-colors" />
    </div>
    <h4 className="font-headings text-xl uppercase tracking-wide text-white leading-none mb-2">{move.name}</h4>
    <p className="text-xs text-white/45 uppercase tracking-wider font-semibold mb-3">{move.skillFocus.join(" · ")}</p>
    <p className="text-sm text-white/60 leading-relaxed line-clamp-2">{move.objective}</p>
  </motion.button>
);

export const MovesGrid = ({ tier }) => {
  const [active, setActive] = useState(null);
  const moves = MOVES.filter((m) => m.tier === tier);
  const openMove = (m) => { setActive(m); track("move_view", { move_id: m.id }); };
  return (
    <div className="my-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {moves.map((m) => <MoveCard key={m.id} move={m} onOpen={openMove} />)}
      </div>
      <MoveDialog move={active} onClose={() => setActive(null)} />
    </div>
  );
};

const Section = ({ icon: Icon, title, children, tone = "white" }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className={tone === "red" ? "text-[#C41E24]" : "text-white/70"} />
      <h5 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70">{title}</h5>
    </div>
    {children}
  </div>
);

const MoveDialog = ({ move, onClose }) => {
  const { videoOverrides } = useSettings();
  if (!move) return null;
  const override = videoOverrides?.[move.id];
  const ytId = youtubeId(override);
  return (
    <Dialog open={!!move} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#111111] border border-white/10 text-white" data-testid="move-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <Stars n={move.difficulty} />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#C41E24] bg-[#C41E24]/10 px-2 py-1 rounded-sm">{TIER_LABEL[move.tier]}</span>
          </div>
          <DialogTitle className="font-headings text-3xl sm:text-4xl uppercase tracking-wide text-white leading-none">{move.name}</DialogTitle>
          <DialogDescription className="text-xs text-white/45 uppercase tracking-wider font-semibold pt-1">{move.skillFocus.join(" · ")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
          <div>
            <DrillCanvas path={move.path} cones={move.cones} label={move.name} />
            {ytId ? (
              <div className="mt-3 rounded-lg overflow-hidden border border-white/10 aspect-video" data-testid={`move-video-${move.id}`}>
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${ytId}`}
                  title={`${move.name} technique video`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <a
                data-testid={`move-video-${move.id}`}
                href={override || ytSearch(move.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-3 flex items-center gap-3 bg-[#1A1A1A] border border-white/10 hover:border-[#C41E24]/60 rounded-lg p-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]"
              >
                <span className="flex-shrink-0 w-10 h-10 rounded-sm bg-[#C41E24] flex items-center justify-center">
                  <PlayCircle size={22} className="text-white" />
                </span>
                <span className="flex-1">
                  <span className="block font-subheadings text-xs font-bold uppercase tracking-widest text-white">Watch The Technique</span>
                  <span className="block text-[11px] text-white/45">{override ? "Coach demo video · watch, then practice the diagram" : "Short demo videos · watch, then practice the diagram"}</span>
                </span>
                <ChevronRight size={16} className="text-white/30 group-hover:text-[#C41E24] transition-colors" />
              </a>
            )}
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div className="bg-[#1A1A1A] border border-white/10 rounded-md p-3">
                <div className="flex items-center gap-1.5 text-white/50 uppercase tracking-wider font-bold mb-1"><User size={12} /> Used By</div>
                <p className="text-white/85 font-semibold">{move.usedBy}</p>
              </div>
              <div className="bg-[#1A1A1A] border border-white/10 rounded-md p-3">
                <div className="flex items-center gap-1.5 text-white/50 uppercase tracking-wider font-bold mb-1"><Zap size={12} /> Match Moment</div>
                <p className="text-white/85">{move.matchMoment}</p>
              </div>
            </div>
          </div>

          <div>
            <Section icon={Target} title="Step-by-Step Execution" tone="red">
              <ol className="space-y-2">
                {move.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-white/80">
                    <span className="flex-shrink-0 w-6 h-6 rounded-sm bg-[#C41E24] text-white font-bold text-xs flex items-center justify-center">{i + 1}</span>
                    <span className="pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section icon={Target} title="Coaching Cues">
              <ul className="grid grid-cols-1 gap-1.5">
                {move.cues.map((c, i) => <li key={i} className="text-sm text-white/70 flex gap-2"><span className="text-[#C41E24]">▸</span>{c}</li>)}
              </ul>
            </Section>

            <Section icon={AlertTriangle} title="Common Mistakes">
              <ul className="grid grid-cols-1 gap-1.5">
                {move.mistakes.map((c, i) => <li key={i} className="text-sm text-white/60 flex gap-2"><span className="text-[#C41E24]">✕</span>{c}</li>)}
              </ul>
            </Section>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#111] border-l-4 border-l-[#C41E24] border border-white/5 rounded-r-md p-4">
            <div className="flex items-center gap-2 mb-2"><Timer size={16} className="text-[#C41E24]" /><h5 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/80">{move.drill.name}</h5></div>
            <p className="text-sm text-white/70">{move.drill.desc}</p>
            <p className="text-xs text-white/45 mt-2 uppercase tracking-wider font-semibold">{move.drill.sets}</p>
          </div>
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#111] border-l-4 border-l-[#EAB308] border border-white/5 rounded-r-md p-4">
            <div className="flex items-center gap-2 mb-2"><Trophy size={16} className="text-[#EAB308]" /><h5 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/80">Elite Challenge</h5></div>
            <p className="text-sm text-white/70">{move.eliteChallenge}</p>
          </div>
        </div>

        <div className="mt-2">
          <h5 className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/70 mb-3">Next O Standards</h5>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[["Bronze", move.standards.bronze, "#CD7F32"], ["Silver", move.standards.silver, "#C0C0C0"], ["Gold", move.standards.gold, "#EAB308"], ["Elite", move.standards.elite, "#C41E24"]].map(([label, val, color]) => (
              <div key={label} className="bg-[#1A1A1A] border border-white/10 rounded-md p-3">
                <div className="font-headings text-2xl text-white">{val}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest mt-1" style={{ color }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoveDialog;
